import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FiSearch,
  FiDownload,
  FiFileText,
  FiUsers,
  FiTruck,
  FiClipboard,
  FiTrendingUp,
  FiRefreshCw,
  FiAlertCircle,
  FiCalendar,
  FiBriefcase,
} from "react-icons/fi";
import {
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
} from "recharts";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import { getCurrentUser } from "../../services/auth_service";
import {
  getFinancialYears,
  getReportsList,
  getReportsSummary,
  getDriverReport,
  getVehicleReport,
  getVehicleDocumentsReport,
  getSupplierReport,
  getAssignmentReport,
  getOutstandingReport,
  getStaffReport,
  logReportDownload,
} from "../../services/report_service";
import "./Reports.css";
const ICON_MAP = {
  "Driver Report": {
    icon: FiUsers,
    color: "blue",
  },
  "Vehicle Report": {
    icon: FiTruck,
    color: "green",
  },
  "Vehicle Documents Report": {
    icon: FiFileText,
    color: "indigo",
  },
  "Supplier Report": {
    icon: FiClipboard,
    color: "purple",
  },
  "Assignment Report": {
    icon: FiFileText,
    color: "orange",
  },
  "Outstanding Report": {
    icon: FiTrendingUp,
    color: "red",
  },
  "Staff Report": {
    icon: FiBriefcase,
    color: "teal",
  },
};
const COLOR_HEX = {
  blue: "#2563eb",
  green: "#16a34a",
  indigo: "#4f46e5",
  purple: "#7c3aed",
  orange: "#ea580c",
  red: "#dc2626",
  teal: "#0d9488",
};
const COLOR_HEX_DARK = {
  blue: "#1e3a8a",
  green: "#166534",
  indigo: "#312e81",
  purple: "#581c87",
  orange: "#9a3412",
  red: "#991b1b",
  teal: "#115e59",
};
const CHART_COLORS = [
  "#7c3aed",
  "#059669",
  "#ea580c",
  "#dc2626",
  "#0891b2",
  "#ca8a04",
  "#4f46e5",
];
const TOOLTIP_STYLE = {
  fontSize: 11,
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
  padding: "6px 10px",
};
const REPORT_FETCH_MAP = {
  "Driver Report": getDriverReport,
  "Vehicle Report": getVehicleReport,
  "Vehicle Documents Report": getVehicleDocumentsReport,
  "Supplier Report": getSupplierReport,
  "Assignment Report": getAssignmentReport,
  "Outstanding Report": getOutstandingReport,
  "Staff Report": getStaffReport,
};
const toInputDate = (val) => {
  if (!val) return "";
  const d = val instanceof Date ? val : new Date(val);
  if (isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const EXCEL_AUTOCONVERT_PATTERNS = [
  /^\d{4,}$/,
  /^\d{1,2} [A-Za-z]{3} \d{4}/,
];
const needsTextForce = (value) => {
  const str = String(value).trim();
  return EXCEL_AUTOCONVERT_PATTERNS.some((re) =>
    re.test(str)
  );
};
const isUrlValue = (value) => {
  if (typeof value !== "string") return false;
  const valueTrimmed = value.trim().toLowerCase();
  return (
    valueTrimmed.startsWith("http://") ||
    valueTrimmed.startsWith("https://") ||
    valueTrimmed.startsWith("www.") ||
    valueTrimmed.startsWith("/uploads/") ||
    valueTrimmed.startsWith("uploads/") ||
    valueTrimmed.includes("/uploads/")
  );
};
const isUrlField = (key) => {
  const normalizedKey = String(key).toLowerCase();
  return (
    normalizedKey === "url" ||
    normalizedKey.endsWith("_url") ||
    normalizedKey.includes("_url_") ||
    normalizedKey.includes("url")
  );
};
const isBooleanField = (key) => {
  const normalizedKey = String(key).toLowerCase();
  return (
    normalizedKey.startsWith("is_") ||
    normalizedKey.startsWith("has_") ||
    normalizedKey.startsWith("can_") ||
    normalizedKey.endsWith("_enabled") ||
    normalizedKey.endsWith("_verified") ||
    normalizedKey.endsWith("_blocked") ||
    normalizedKey.endsWith("_deleted")
  );
};
const csvCell = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "";
  }
  const str = String(value);
  const escaped = str.replace(/"/g, '\\\\"\\\\"');
  return needsTextForce(value)
    ? `="${escaped}"`
    : `"${escaped}"`;
};
const getExportColumns = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }
  const allKeys = [];
  data.forEach((row) => {
    if (!row || typeof row !== "object") return;
    Object.keys(row).forEach((key) => {
      if (!allKeys.includes(key)) {
        allKeys.push(key);
      }
    });
  });
  return allKeys.filter((key) => {
    const values = data.map((row) => row?.[key]);
    if (isUrlField(key) || isBooleanField(key)) {
      return false;
    }
    const populatedValues = values.filter(
      (value) => value !== null && value !== undefined && value !== ""
    );
    if (
      populatedValues.length > 0 &&
      populatedValues.every(
        (value) => typeof value === "boolean"
      )
    ) {
      return false;
    }
    if (populatedValues.some((value) => isUrlValue(value))) {
      return false;
    }
    return true;
  });
};
const HEADER_LABELS = {
  id: "ID",
  driver_code: "Driver Code",
  full_name: "Full Name",
  mobile: "Mobile",
  email: "Email",
  aadhaar_number: "Aadhaar Number",
  adhaar_number: "Aadhaar Number",
  pancard_number: "PAN Number",
  license_number: "License Number",
  license_expiry: "License Expiry",
  date_of_birth: "Date of Birth",
  joining_date: "Joining Date",
  blood_group: "Blood Group",
  character_nature: "Character Nature",
  permanent_address: "Permanent Address",
  temporary_address: "Temporary Address",
  current_address: "Current Address",
  address: "Address",
  city: "City",
  state: "State",
  pincode: "Pincode",
  reference_person_name: "Reference Person Name",
  reference_contact_number_1: "Reference Contact Number 1",
  reference_contact_number_2: "Reference Contact Number 2",
  emergency_contact_name: "Emergency Contact Name",
  emergency_contact_number: "Emergency Contact Number",
  alternate_mobile: "Alternate Mobile",
  max_vehicle_capacity: "Maximum Vehicle Capacity",
  agreement_start_date: "Agreement Start Date",
  agreement_end_date: "Agreement End Date",
  agreement_status: "Agreement Status",
  agreement_tenure_months: "Agreement Tenure (Months)",
  emi_completed_months: "EMI Completed (Months)",
  remarks: "Remarks",
  outstanding_amount: "Outstanding Amount",
  vehicle_code: "Vehicle Code",
  vehicle_id: "Vehicle ID",
  vehicle_registration_number: "Registration Number",
  vehicle_display_number: "Vehicle Display Number",
  vehicle_no: "Vehicle Number",
  display_no: "Vehicle Display Number",
  year_of_make: "Year of Make",
  engine_number: "Engine Number",
  chassis_number: "Chassis Number",
  average_mileage: "Average Mileage",
  seating_capacity: "Seating Capacity",
  registration_date: "Registration Date",
  vehicle_make_name: "Vehicle Make",
  vehicle_model_name: "Vehicle Model",
  fuel_type_name: "Fuel Type",
  fuel_issue_name: "Fuel Issue",
  transmission_name: "Transmission",
  tax_status_name: "Tax Status",
  company_name: "Company",
  display_code: "Display Code",
  rc_expiry_date: "RC Expiry Date",
  permit_expiry_date: "Permit Expiry Date",
  fitness_expiry_date: "Fitness Expiry Date",
  insurance_expiry_date: "Insurance Expiry Date",
  tax_expiry_date: "Tax Expiry Date",
  pollution_expiry_date: "Pollution Expiry Date",
  police_sticker_expiry_date: "Police Sticker Expiry Date",
  city_police_permit_expiry_date: "City Police Permit Expiry Date",
  unique_number: "Assignment Number",
  assigned_date: "Assigned Date",
  relieved_date: "Relieved Date",
  vehicle_odometer_km: "Vehicle Odometer (KM)",
  transaction_id: "Transaction ID",
  transaction_date: "Transaction Date",
  chain_id: "Chain ID",
  driver_name: "Driver Name",
  supplier_name: "Supplier Name",
  supplier_code: "Supplier Code",
  supplier_id: "Supplier ID",
  assignment_type: "Assignment Type",
  parent_assignment_number: "Parent Assignment Number",
  supplier_type_name: "Supplier Type",
  status_name: "Status",
  service_location_name: "Service Location",
  location_name: "Location",
  branch_name: "Branch",
  financial_year: "Financial Year",
  linked_user_name: "Linked User",
  created_by_name: "Created By",
  updated_by_name: "Updated By",
  created_at: "Created At",
  updated_at: "Updated At",
  staff_code: "Staff Code",
  designation: "Designation",
  role_name: "Role",
  status: "Status",
  last_login: "Last Login",
};
const formatHeader = (key) => {
  if (HEADER_LABELS[key]) {
    return HEADER_LABELS[key];
  }
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};
const formatExportValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "number") {
    return value;
  }
  if (value instanceof Date) {
    return value.toLocaleDateString("en-IN");
  }
  return String(value)
    .replace(/\r?\n|\r/g, " ")
    .trim();
};
const fmtNum = (n) =>
  Number(n || 0).toLocaleString();
export default function Reports() {
  const currentUser = getCurrentUser();
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [downloading, setDownloading] = useState(null);
  const [financialYears, setFinancialYears] = useState([]);
  const [financialYearId, setFinancialYearId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const selectedFy =
    financialYears.find(
      (y) => String(y.id) === financialYearId
    ) || null;
  const loadFinancialYears = useCallback(async () => {
    try {
      const data = await getFinancialYears();
      setFinancialYears(data || []);
    } catch (err) {
      console.error(
        "Failed to load financial years:",
        err
      );
    }
  }, []);
  const handleFyChange = (id) => {
    setFinancialYearId(id);
    if (!id) {
      setDateFrom("");
      setDateTo("");
      return;
    }
    const fy = financialYears.find(
      (item) => String(item.id) === id
    );
    setDateFrom(
      fy ? toInputDate(fy.start_date) : ""
    );
    setDateTo(
      fy ? toInputDate(fy.end_date) : ""
    );
  };
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [reportData, summaryData] =
        await Promise.all([
          getReportsList(
            search,
            "",
            financialYearId,
            dateFrom,
            dateTo
          ),
          getReportsSummary(
            financialYearId,
            dateFrom,
            dateTo
          ),
        ]);
      setReports(reportData || []);
      setSummary(summaryData || null);
    } catch (err) {
      console.error(err);
      setError(
        "Failed to load reports. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [
    search,
    financialYearId,
    dateFrom,
    dateTo,
  ]);
  useEffect(() => {
    loadFinancialYears();
  }, [loadFinancialYears]);
  useEffect(() => {
    loadData();
  }, [loadData]);
  const chartData = useMemo(() => {
    const byType = {};
    const byMonth = {};
    reports.forEach((item) => {
      const type = item.report;
      const records = Number(item.records) || 0;
      if (!byType[type]) {
        byType[type] = {
          name: type,
          count: 0,
          records: 0,
        };
      }
      byType[type].count += 1;
      byType[type].records += records;
      const d = new Date(item.generated);
      if (!isNaN(d.getTime())) {
        const label = d.toLocaleString(
          "default",
          {
            month: "short",
            year: "numeric",
          }
        );
        const sortKey =
          d.getFullYear() * 12 +
          d.getMonth();
        if (!byMonth[label]) {
          byMonth[label] = {
            month: label,
            sortKey,
            count: 0,
            records: 0,
          };
        }
        byMonth[label].count += 1;
        byMonth[label].records += records;
      }
    });
    const typeData = Object.values(byType);
    const monthData = Object.values(byMonth).sort(
      (a, b) => a.sortKey - b.sortKey
    );
    const totalCount = typeData.reduce(
      (sum, item) => sum + item.count,
      0
    );
    const totalRecords = typeData.reduce(
      (sum, item) => sum + item.records,
      0
    );
    const rankedByRecords = [...typeData].sort(
      (a, b) => b.records - a.records
    );
    const maxRecords = rankedByRecords.length
      ? rankedByRecords[0].records
      : 0;
    return {
      typeData,
      monthData,
      totalCount,
      totalRecords,
      rankedByRecords,
      maxRecords,
    };
  }, [reports]);
  const activeFilterCount = [
    search,
    financialYearId,
    dateFrom,
    dateTo,
  ].filter(Boolean).length;
  const handleReset = () => {
    setSearch("");
    setFinancialYearId("");
    setDateFrom("");
    setDateTo("");
  };
  const handleDownload = async (item) => {
    try {
      setDownloading(item.report);
      const fetchFn =
        REPORT_FETCH_MAP[item.report];
      const data = fetchFn
        ? await fetchFn(
            financialYearId,
            dateFrom,
            dateTo
          )
        : [];
      const csvContent = buildCSV(
        data,
        item.report
      );
      triggerCSVDownload(
        csvContent,
        item.report
      );
      await logReportDownload(
        currentUser?.user_id ??
          currentUser?.id ??
          11,
        item.report,
        "CSV",
        financialYearId || null
      );
    } catch (err) {
      console.error(err);
      alert(
        "Download failed. Please try again."
      );
    } finally {
      setDownloading(null);
    }
  };
  const buildCSV = (data, reportName) => {
    if (!Array.isArray(data) || data.length === 0) {
      return "No data available";
    }
    const keys = getExportColumns(data);
    if (keys.length === 0) {
      return "No exportable data available";
    }
    const headers = keys
      .map((key) => csvCell(formatHeader(key)))
      .join(",");
    const rows = data.map((row) =>
      keys
        .map((key) =>
          csvCell(formatExportValue(row?.[key]))
        )
        .join(",")
    );
    return [headers, ...rows].join("\n");
  };
  const triggerCSVDownload = (
    csvContent,
    reportName
  ) => {
    const blob = new Blob(
      [csvContent],
      {
        type: "text/csv;charset=utf-8;",
      }
    );
    const url =
      URL.createObjectURL(blob);
    const link =
      document.createElement("a");
    link.href = url;
    link.download = `${reportName.replace(
      / /g,
      "_"
    )}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const summaryCards = [
    {
      icon: FiUsers,
      label: "Driver Reports",
      value:
        summary?.driver_reports ?? "—",
      color: "blue",
    },
    {
      icon: FiTruck,
      label: "Vehicle Reports",
      value:
        summary?.vehicle_reports ?? "—",
      color: "green",
    },
    {
      icon: FiFileText,
      label: "Vehicle Documents",
      value:
        summary?.vehicle_documents_reports ??
        "—",
      color: "indigo",
    },
    {
      icon: FiClipboard,
      label: "Supplier Reports",
      value:
        summary?.supplier_reports ?? "—",
      color: "purple",
    },
    {
      icon: FiFileText,
      label: "Assignments",
      value:
        summary?.assignment_reports ?? "—",
      color: "orange",
    },
    {
      icon: FiTrendingUp,
      label: "Outstanding",
      value:
        summary?.outstanding_reports ?? "—",
      color: "red",
    },
    {
      icon: FiBriefcase,
      label: "Staff Reports",
      value:
        summary?.staff_reports ?? "—",
      color: "teal",
    },
  ];
  return (
    <div className="layout">
      <Sidebar />
      <div className="content">
        <Header />
        <div className="rpt-page">
          {error && (
            <div className="rpt-error">
              <FiAlertCircle size={15} />
              {error}
            </div>
          )}
          <div className="rpt-summary-grid">
            {summaryCards.map(
              ({
                icon: Icon,
                label,
                value,
                color,
              }) => (
                <div
                  key={label}
                  className={`rpt-card rpt-card--${color}`}
                >
                  <div className="rpt-card-icon">
                    <Icon size={20} />
                  </div>
                  <div className="rpt-card-body">
                    <span className="rpt-card-value">
                      {loading ? (
                        <span className="skeleton-val" />
                      ) : (
                        value
                      )}
                    </span>
                    <span className="rpt-card-label">
                      {label}
                    </span>
                  </div>
                </div>
              )
            )}
          </div>
          <div className="rpt-filters">
            <div className="rpt-search-wrap rpt-search-wrap--sticky">
              <FiSearch
                size={14}
                className="rpt-search-icon"
              />
              <input
                type="text"
                placeholder="Search Reports"
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
                className="rpt-search-input"
              />
            </div>
            <div className="rpt-fy-wrap">
              <FiCalendar size={13} />
              <select
                className="rpt-fy-select"
                value={financialYearId}
                onChange={(e) =>
                  handleFyChange(
                    e.target.value
                  )
                }
              >
                <option value="">
                  All Financial Years
                </option>
                {financialYears.map((fy) => (
                  <option
                    key={fy.id}
                    value={String(fy.id)}
                  >
                    {fy.fy_label}
                  </option>
                ))}
              </select>
            </div>
            <div className="rpt-date-wrap rpt-date-from">
              <FiCalendar size={13} />
              <input
                type="date"
                className="rpt-date-input"
                value={dateFrom}
                min={
                  selectedFy
                    ? toInputDate(
                        selectedFy.start_date
                      )
                    : undefined
                }
                max={
                  dateTo ||
                  (selectedFy
                    ? toInputDate(
                        selectedFy.end_date
                      )
                    : undefined)
                }
                onChange={(e) =>
                  setDateFrom(
                    e.target.value
                  )
                }
                title="From date"
              />
            </div>
            <div className="rpt-date-wrap rpt-date-to">
              <FiCalendar size={13} />
              <input
                type="date"
                className="rpt-date-input"
                value={dateTo}
                min={
                  dateFrom ||
                  (selectedFy
                    ? toInputDate(
                        selectedFy.start_date
                      )
                    : undefined)
                }
                max={
                  selectedFy
                    ? toInputDate(
                        selectedFy.end_date
                      )
                    : undefined
                }
                onChange={(e) =>
                  setDateTo(
                    e.target.value
                  )
                }
                title="To date"
              />
            </div>
            {activeFilterCount > 0 && (
              <button
                className="rpt-reset-icon-btn"
                onClick={handleReset}
                title="Reset filters"
                aria-label="Reset filters"
              >
                <FiRefreshCw size={14} />
              </button>
            )}
          </div>
          <div className="rpt-table-card">
            <div className="rpt-table-head">
              <h3>Generated Reports</h3>
              <span className="rpt-count">
                {reports.length} report
                {reports.length !== 1
                  ? "s"
                  : ""}
              </span>
            </div>
            <div className="rpt-table-wrap">
              <table className="rpt-table">
                <thead>
                  <tr>
                    <th>Report Name</th>
                    <th>Generated</th>
                    <th>Records</th>
                    <th>Status</th>
                    <th>Download</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({
                      length: 7,
                    }).map((_, i) => (
                      <tr
                        key={i}
                        className="skeleton-row"
                      >
                        <td>
                          <span className="skeleton" />
                        </td>
                        <td>
                          <span className="skeleton" />
                        </td>
                        <td>
                          <span className="skeleton" />
                        </td>
                        <td>
                          <span className="skeleton" />
                        </td>
                        <td>
                          <span className="skeleton" />
                        </td>
                      </tr>
                    ))
                  ) : reports.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="rpt-empty"
                      >
                        <FiFileText size={32} />
                        <p>
                          No reports match your
                          search
                        </p>
                      </td>
                    </tr>
                  ) : (
                    reports.map((item) => {
                      const meta =
                        ICON_MAP[
                          item.report
                        ] || {
                          icon: FiFileText,
                          color: "blue",
                        };
                      const Icon = meta.icon;
                      const isDownloading =
                        downloading ===
                        item.report;
                      return (
                        <tr key={item.id}>
                          <td>
                            <div className="rpt-name-cell">
                              <span
                                className={`rpt-type-dot rpt-type-dot--${meta.color}`}
                              >
                                <Icon size={12} />
                              </span>
                              {item.report}
                            </div>
                          </td>
                          <td className="rpt-date-cell">
                            {item.generated}
                          </td>
                          <td>
                            <span className="rpt-records-badge">
                              {item.records}
                            </span>
                          </td>
                          <td>
                            <span className="rpt-status-badge completed">
                              {item.status}
                            </span>
                          </td>
                          <td>
                            <button
                              className="rpt-dl-btn"
                              title={`Download ${item.report}`}
                              onClick={() =>
                                handleDownload(
                                  item
                                )
                              }
                              disabled={
                                isDownloading
                              }
                            >
                              {isDownloading ? (
                                <FiRefreshCw
                                  size={13}
                                  className="spin"
                                />
                              ) : (
                                <FiDownload
                                  size={13}
                                />
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {!loading &&
            chartData.typeData.length > 0 && (
              <div className="rpt-charts-grid">
                <div className="rpt-chart-card">
                  <div className="rpt-chart-head">
                    <div className="rpt-chart-head-icon rpt-chart-head-icon--indigo">
                      <FiClipboard size={13} />
                    </div>
                    <div>
                      <h3>
                        Report Type
                        Distribution
                      </h3>
                      <p>
                        Share of records by
                        type
                      </p>
                    </div>
                  </div>
                  <div className="rpt-donut-wrap">
                    <ResponsiveContainer
                      width="100%"
                      height={200}
                    >
                      <PieChart>
                        <Pie
                          data={
                            chartData.typeData
                          }
                          dataKey="records"
                          nameKey="name"
                          innerRadius={54}
                          outerRadius={82}
                          paddingAngle={4}
                          cornerRadius={5}
                          stroke="none"
                        >
                          {chartData.typeData.map(
                            (
                              entry,
                              index
                            ) => (
                              <Cell
                                key={
                                  entry.name
                                }
                                fill={
                                  CHART_COLORS[
                                    index %
                                      CHART_COLORS.length
                                  ]
                                }
                              />
                            )
                          )}
                        </Pie>
                        <Tooltip
                          contentStyle={
                            TOOLTIP_STYLE
                          }
                          formatter={(
                            value,
                            name
                          ) => [
                            `${fmtNum(
                              value
                            )} record${
                              value !== 1
                                ? "s"
                                : ""
                            }`,
                            name,
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="rpt-donut-center">
                      <span className="rpt-donut-center-value">
                        {fmtNum(
                          chartData.totalRecords
                        )}
                      </span>
                      <span className="rpt-donut-center-label">
                        Total
                      </span>
                    </div>
                  </div>
                  <div className="rpt-legend-chips">
                    {chartData.typeData.map(
                      (
                        entry,
                        index
                      ) => {
                        const color =
                          CHART_COLORS[
                            index %
                              CHART_COLORS.length
                          ];
                        return (
                          <span
                            className="rpt-legend-chip"
                            key={entry.name}
                          >
                            <span
                              className="rpt-legend-dot"
                              style={{
                                background:
                                  color,
                              }}
                            />
                            {entry.name.replace(
                              " Report",
                              ""
                            )}
                            <span className="rpt-legend-count">
                              {fmtNum(
                                entry.records
                              )}
                            </span>
                          </span>
                        );
                      }
                    )}
                  </div>
                </div>
                <div className="rpt-chart-card">
                  <div className="rpt-chart-head">
                    <div className="rpt-chart-head-icon rpt-chart-head-icon--green">
                      <FiTrendingUp size={13} />
                    </div>
                    <div>
                      <h3>
                        Records by Report
                        Type
                      </h3>
                      <p>
                        Ranked by total
                        records processed
                      </p>
                    </div>
                  </div>
                  <div className="rpt-rank-list">
                    {chartData.rankedByRecords.map(
                      (entry) => {
                        const meta =
                          ICON_MAP[
                            entry.name
                          ] || {
                            icon: FiFileText,
                            color: "blue",
                          };
                        const Icon =
                          meta.icon;
                        const color =
                          COLOR_HEX[
                            meta.color
                          ] ||
                          "#6366f1";
                        const colorDark =
                          COLOR_HEX_DARK[
                            meta.color
                          ] ||
                          "#4338ca";
                        const pct =
                          chartData.maxRecords >
                          0
                            ? Math.max(
                                (entry.records /
                                  chartData.maxRecords) *
                                  100,
                                entry.records >
                                  0
                                  ? 4
                                  : 0
                              )
                            : 0;
                        return (
                          <div
                            className="rpt-rank-row"
                            key={
                              entry.name
                            }
                          >
                            <span
                              className="rpt-rank-icon"
                              style={{
                                background: `${color}1a`,
                                color,
                              }}
                            >
                              <Icon size={12} />
                            </span>
                            <div className="rpt-rank-info">
                              <div className="rpt-rank-top">
                                <span className="rpt-rank-name">
                                  {
                                    entry.name
                                  }
                                </span>
                                <span className="rpt-rank-value">
                                  {fmtNum(
                                    entry.records
                                  )}
                                </span>
                              </div>
                              <div className="rpt-rank-track">
                                <div
                                  className="rpt-rank-fill"
                                  style={{
                                    width: `${pct}%`,
                                    background: `linear-gradient(90deg, ${color}, ${colorDark})`,
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
                <div className="rpt-chart-card rpt-chart-card--wide">
                  <div className="rpt-chart-head">
                    <div className="rpt-chart-head-icon rpt-chart-head-icon--indigo">
                      <FiCalendar size={13} />
                    </div>
                    <div>
                      <h3>
                        Reports Generated
                        Over Time
                      </h3>
                      <p>
                        Monthly record volume
                        trend
                      </p>
                    </div>
                  </div>
                  {chartData.monthData.length <
                  2 ? (
                    <div className="rpt-chart-single-point">
                      <div
                        className="rpt-single-bg-bars"
                        aria-hidden="true"
                      >
                        {[
                          38,
                          62,
                          45,
                          80,
                          52,
                          70,
                          46,
                        ].map(
                          (h, i) => (
                            <span
                              key={i}
                              style={{
                                height: `${h}%`,
                              }}
                            />
                          )
                        )}
                      </div>
                      <span className="rpt-single-icon-badge">
                        <FiTrendingUp
                          size={18}
                        />
                      </span>
                      <span className="rpt-chart-single-value">
                        {fmtNum(
                          chartData
                            .monthData[0]
                            ?.records ??
                            0
                        )}
                      </span>
                      <span className="rpt-chart-single-label">
                        records generated in{" "}
                        {chartData
                          .monthData[0]
                          ?.month ??
                          "this period"}
                        <br />
                        Trend line unlocks
                        once a second
                        month of history
                        is available.
                      </span>
                    </div>
                  ) : (
                    <ResponsiveContainer
                      width="100%"
                      height={230}
                    >
                      <AreaChart
                        data={
                          chartData.monthData
                        }
                        margin={{
                          top: 5,
                          right: 10,
                          left: -18,
                          bottom: 5,
                        }}
                      >
                        <defs>
                          <linearGradient
                            id="rptTrendFill"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor="#0f766e"
                              stopOpacity={
                                0.34
                              }
                            />
                            <stop
                              offset="100%"
                              stopColor="#0f766e"
                              stopOpacity={
                                0.03
                              }
                            />
                          </linearGradient>
                        </defs>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          vertical={false}
                          stroke="#eceff1"
                        />
                        <XAxis
                          dataKey="month"
                          tick={{
                            fontSize: 9,
                            fill: "#6b7280",
                          }}
                          axisLine={{
                            stroke:
                              "#e5e7eb",
                          }}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{
                            fontSize: 9,
                            fill: "#6b7280",
                          }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={
                            TOOLTIP_STYLE
                          }
                          formatter={(
                            value
                          ) => [
                            fmtNum(value),
                            "Records",
                          ]}
                        />
                        <Area
                          type="monotone"
                          dataKey="records"
                          stroke="#0f766e"
                          strokeWidth={2.8}
                          fill="url(#rptTrendFill)"
                          dot={{
                            r: 3.5,
                            fill: "#0f766e",
                            strokeWidth: 0,
                          }}
                          activeDot={{
                            r: 5,
                          }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
                <div className="rpt-chart-card rpt-chart-card--wide rpt-chart-card--colorful">
                  <div className="rpt-chart-head">
                    <div className="rpt-chart-head-icon rpt-chart-head-icon--orange">
                      <FiTrendingUp size={13} />
                    </div>
                    <div>
                      <h3>
                        Reports Generated
                      </h3>
                      <p>
                        Report count by
                        month
                      </p>
                    </div>
                  </div>
                  <ResponsiveContainer
                    width="100%"
                    height={230}
                  >
                    <BarChart
                      data={
                        chartData.monthData
                      }
                      margin={{
                        top: 5,
                        right: 10,
                        left: -18,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#f0f1f4"
                      />
                      <XAxis
                        dataKey="month"
                        tick={{
                          fontSize: 9,
                          fill: "#6b7280",
                        }}
                        axisLine={{
                          stroke:
                            "#e5e7eb",
                        }}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{
                          fontSize: 9,
                          fill: "#6b7280",
                        }}
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={
                          TOOLTIP_STYLE
                        }
                        formatter={(
                          value
                        ) => [
                          fmtNum(value),
                          "Reports",
                        ]}
                      />
                      <Bar
                        dataKey="count"
                        radius={[
                          6,
                          6,
                          0,
                          0,
                        ]}
                      >
                        {chartData.monthData.map(
                          (
                            entry,
                            index
                          ) => (
                            <Cell
                              key={
                                entry.month
                              }
                              fill={
                                CHART_COLORS[
                                  index %
                                    CHART_COLORS.length
                                ]
                              }
                            />
                          )
                        )}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}